<template>
  <div class="league-rankings-page">
    <header class="league-rankings-header">
      <v-btn icon class="back-btn" @click="goBack">
        <v-icon>arrow_back</v-icon>
      </v-btn>
      <div class="league-rankings-title-wrap">
        <img
          :src="(leagueData && leagueData.Logo) || defaultLeagueLogo"
          :alt="leagueName"
          class="league-rankings-logo"
          @error="onLeagueLogoError"
        />
        <h1 class="league-rankings-title">{{ leagueName || "League" }}</h1>
        <p class="league-rankings-subtitle">Weight class rankings</p>
      </div>
    </header>

    <div class="league-rankings-content">
      <div v-if="loading" class="skeleton-weight-links">
        <skeleton-loader type="card" />
        <skeleton-loader type="card" />
        <skeleton-loader type="card" />
        <skeleton-loader type="card" />
      </div>
      <div
        v-else-if="weightclasses && weightclasses.length"
        class="league-rankings-main"
      >
        <div class="weight-links">
          <!-- Left column: Pound for Pound + Rising Stars -->
          <div class="rankings-col rankings-col-p4p">
            <div class="weight-class-block pound-block">
              <div class="weight-link-card weight-link-card--stacked-heading">
                <span class="weight-link-label">Pound for Pound</span>
                <span class="pound-subtitle"
                  >Top 5 wrestlers across all weights</span
                >
              </div>
              <div class="top-five-wrap">
                <div v-if="!authenticated" class="top-five-empty">
                  You must be logged in to see this data.
                </div>
                <template v-else>
                  <div v-if="poundLoading" class="top-five-loading">
                    <v-progress-circular indeterminate size="24" />
                    <span>Loading…</span>
                  </div>
                  <div
                    v-else-if="poundTop && poundTop.length === 0"
                    class="top-five-empty"
                  >
                    No wrestlers in this league yet.
                  </div>
                  <div v-else class="top-five-list">
                    <div
                      v-for="(w, idx) in poundTop"
                      :key="w.id"
                      class="top-five-row"
                      @click="goToWrestler(w.id)"
                    >
                      <span class="top-five-rank">{{ idx + 1 }}</span>
                      <img
                        v-if="
                          authenticated && w.data.Image && w.data.Image !== ''
                        "
                        :src="w.data.Image"
                        class="top-five-avatar"
                        alt=""
                      />
                      <img
                        v-else
                        :src="require('../assets/default-profile.png')"
                        class="top-five-avatar"
                        alt=""
                      />
                      <div class="top-five-info">
                        <span class="top-five-name">
                          {{ w.data.FirstName }} {{ w.data.LastName }}
                        </span>
                        <div
                          class="top-five-skill-bar skill-bar skill-bar--linear"
                          :aria-label="
                            'Skill rating ' +
                            formatSkillLevel(
                              w.data && w.data.Skill != null ? w.data.Skill : 0
                            )
                          "
                        >
                          <div class="skill-bar-linear-track">
                            <div
                              class="skill-bar-linear-fill"
                              :style="{
                                width: poundSkillBarPercent(w) + '%',
                              }"
                            ></div>
                          </div>
                          <span class="skill-bar-label">{{
                            formatSkillLevel(
                              w.data && w.data.Skill != null ? w.data.Skill : 0
                            )
                          }}</span>
                        </div>
                        <span class="top-five-record">
                          ({{ w.data.Wins || 0 }}–{{ w.data.Losses || 0 }})
                        </span>
                      </div>
                      <v-icon class="top-five-chevron" small
                        >chevron_right</v-icon
                      >
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <div class="weight-class-block pound-block">
              <div class="weight-link-card weight-link-card--stacked-heading">
                <span class="weight-link-label">Rising Stars</span>
                <span class="pound-subtitle"
                  >Top 5 by skill gain over last 3 matches</span
                >
              </div>
              <div class="top-five-wrap">
                <div v-if="!authenticated" class="top-five-empty">
                  You must be logged in to see this data.
                </div>
                <template v-else>
                  <div v-if="risingLoading" class="top-five-loading">
                    <v-progress-circular indeterminate size="24" />
                    <span>Loading…</span>
                  </div>
                  <div
                    v-else-if="risingTop && risingTop.length === 0"
                    class="top-five-empty"
                  >
                    No recent skill gains in this league yet.
                  </div>
                  <div v-else class="top-five-list">
                    <div
                      v-for="(w, idx) in risingTop"
                      :key="w.id"
                      class="top-five-row"
                      @click="goToWrestler(w.id)"
                    >
                      <span class="top-five-rank">{{ idx + 1 }}</span>
                      <img
                        v-if="
                          authenticated && w.data.Image && w.data.Image !== ''
                        "
                        :src="w.data.Image"
                        class="top-five-avatar"
                        alt=""
                      />
                      <img
                        v-else
                        :src="require('../assets/default-profile.png')"
                        class="top-five-avatar"
                        alt=""
                      />
                      <div class="top-five-info">
                        <span class="top-five-name">
                          {{ w.data.FirstName }} {{ w.data.LastName }}
                        </span>
                        <div
                          class="top-five-skill-bar skill-bar skill-bar--linear"
                          :aria-label="
                            'Skill rating ' +
                            formatSkillLevel(
                              w.data && w.data.Skill != null ? w.data.Skill : 0
                            )
                          "
                        >
                          <div class="skill-bar-linear-track">
                            <div
                              class="skill-bar-linear-fill"
                              :style="{
                                width: risingSkillBarPercent(w) + '%',
                              }"
                            ></div>
                          </div>
                          <span class="skill-bar-label">{{
                            formatSkillLevel(
                              w.data && w.data.Skill != null ? w.data.Skill : 0
                            )
                          }}</span>
                        </div>
                        <span class="top-five-record">
                          ({{ w.data.Wins || 0 }}–{{ w.data.Losses || 0 }}) ·
                          last 3: {{ formatSkillChangeInline(w.delta) }}
                        </span>
                      </div>
                      <div
                        v-if="w.trendPoints && w.trendPoints.length > 0"
                        class="rising-sparkline-wrap"
                      >
                        <svg
                          class="rising-sparkline"
                          viewBox="0 0 80 24"
                          preserveAspectRatio="none"
                        >
                          <path
                            :d="risingSparklinePath(w.trendPoints)"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <v-icon class="top-five-chevron" small
                        >chevron_right</v-icon
                      >
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Center column (wide): Weight classes -->
          <div class="rankings-col rankings-col-weightclasses">
            <div class="weight-classes-inner">
              <div
                v-for="wc in weightclasses"
                :key="wc.id || wc.Weight"
                class="weight-class-block"
              >
                <div class="weight-link-card">
                  <span class="weight-link-label">{{ wc.Weight }}</span>
                  <div class="weight-link-actions">
                    <v-btn
                      icon
                      small
                      class="expand-btn"
                      :aria-label="isExpanded(wc) ? 'Collapse' : 'Show top 5'"
                      @click="toggleExpand(wc)"
                    >
                      <v-icon small>{{
                        isExpanded(wc) ? "expand_less" : "expand_more"
                      }}</v-icon>
                    </v-btn>
                    <router-link
                      :to="weightRoute(wc)"
                      class="full-rankings-btn"
                    >
                      See full rankings
                    </router-link>
                  </div>
                </div>
                <v-expand-transition>
                  <div v-show="isExpanded(wc)" class="top-five-wrap">
                    <div
                      v-if="loadingTopFive[weightKey(wc)]"
                      class="top-five-loading"
                    >
                      <v-progress-circular indeterminate size="24" />
                      <span>Loading…</span>
                    </div>
                    <div
                      v-else-if="(getTopFive(wc) || []).length === 0"
                      class="top-five-empty"
                    >
                      No wrestlers in this weight class.
                    </div>
                    <div v-else class="top-five-list">
                      <div
                        v-for="(w, idx) in getTopFive(wc)"
                        :key="w.id"
                        class="top-five-row"
                        @click="goToWrestler(w.id)"
                      >
                        <span class="top-five-rank">{{ idx + 1 }}</span>
                        <img
                          v-if="
                            authenticated && w.data.Image && w.data.Image !== ''
                          "
                          :src="w.data.Image"
                          class="top-five-avatar"
                          alt=""
                        />
                        <img
                          v-else
                          :src="require('../assets/default-profile.png')"
                          class="top-five-avatar"
                          alt=""
                        />
                        <div class="top-five-info">
                          <span class="top-five-name">
                            {{ w.data.FirstName }} {{ w.data.LastName }}
                          </span>
                          <div
                            class="top-five-skill-bar skill-bar skill-bar--linear"
                            :aria-label="
                              'Skill rating ' +
                              formatSkillLevel(
                                w.data && w.data.Skill != null
                                  ? w.data.Skill
                                  : 0
                              )
                            "
                          >
                            <div class="skill-bar-linear-track">
                              <div
                                class="skill-bar-linear-fill"
                                :style="{
                                  width:
                                    weightTopFiveSkillBarPercent(w, wc) + '%',
                                }"
                              ></div>
                            </div>
                            <span class="skill-bar-label">{{
                              formatSkillLevel(
                                w.data && w.data.Skill != null
                                  ? w.data.Skill
                                  : 0
                              )
                            }}</span>
                          </div>
                          <span class="top-five-record">
                            ({{ w.data.Wins || 0 }}–{{ w.data.Losses || 0 }})
                          </span>
                        </div>
                        <v-icon class="top-five-chevron" small
                          >chevron_right</v-icon
                        >
                      </div>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </div>

          <!-- Right column (wide): Team rankings -->
          <div class="rankings-col rankings-col-team">
            <div class="weight-class-block pound-block team-rankings-block">
              <div class="weight-link-card weight-link-card--stacked-heading">
                <span class="weight-link-label">Team rankings</span>
                <span class="team-rankings-skill-hint caption grey--text">{{
                  teamRankingsHeaderSubtitle
                }}</span>
                <span
                  v-if="teamRankingsCacheStamp"
                  class="team-rankings-cache-stamp caption grey--text text--darken-1"
                  >{{ teamRankingsCacheStamp }}</span
                >
              </div>
              <div class="top-five-wrap">
                <div v-if="!authenticated" class="top-five-empty">
                  You must be logged in to see this data.
                </div>
                <template v-else>
                  <div v-if="teamLoading" class="top-five-loading">
                    <v-progress-circular indeterminate size="24" />
                    <span>Loading…</span>
                  </div>
                  <div
                    v-else-if="teamTop && teamTop.length === 0"
                    class="top-five-empty"
                  >
                    No teams with ranked wrestlers in this league yet.
                  </div>
                  <div v-else class="top-five-list">
                    <div
                      v-for="(t, idx) in teamTop"
                      :key="t.teamKey"
                      class="team-ranking-row team-ranking-row--clickable"
                      role="button"
                      tabindex="0"
                      @click="goToTeamLineup(t)"
                      @keydown.enter.prevent="goToTeamLineup(t)"
                      @keydown.space.prevent="goToTeamLineup(t)"
                    >
                      <span class="top-five-rank">{{ idx + 1 }}</span>
                      <div class="top-five-info">
                        <span class="top-five-name">{{ t.schoolDisplay }}</span>
                        <div
                          class="top-five-skill-bar skill-bar skill-bar--linear"
                          :aria-label="
                            'Team average skill ' + formatSkillLevel(t.avgSkill)
                          "
                        >
                          <div class="skill-bar-linear-track">
                            <div
                              class="skill-bar-linear-fill"
                              :style="{
                                width: teamSkillBarPercent(t) + '%',
                              }"
                            ></div>
                          </div>
                          <span class="skill-bar-label">{{
                            formatSkillLevel(t.avgSkill)
                          }}</span>
                        </div>
                        <span class="top-five-record">
                          {{ t.weightsUsed }} weight{{
                            t.weightsUsed === 1 ? "" : "s"
                          }}
                        </span>
                      </div>
                      <v-icon class="top-five-chevron" small
                        >chevron_right</v-icon
                      >
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="empty-state-wrap">
        <p class="empty-state">No weight classes in this league.</p>
      </div>
    </div>
    <FloatingNav />
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import FloatingNav from "../components/FloatingNav.vue";
import { sortWrestlersByRankingRules } from "../utils/rankingSort";
import {
  fetchAllAthletesAtLeagueWeightOrderedBySkill,
  fetchTopAthleteForSchoolAtWeight,
  rankWeightValueFromWc,
} from "../utils/fetchTopAthleteSchoolAtWeight";

/** Recent matches per athlete when resolving H2H ties (league summary pages only). */
const LEAGUE_PAGE_MATCH_FETCH = 48;
/** Max athletes to prefetch before sort / season filter (not full division rosters). */
const P4P_PREFETCH = 40;
const RISING_PREFETCH_EACH_END = 45;
const TOP_FIVE_PREFETCH = 72;
import {
  seasonFilterState,
  filterAthleteRowsBySeason,
  isAllSeasons,
  seasonShortToStoredVariants,
  normalizeSeasonToShort,
} from "../utils/seasonFilter";
import {
  formatSkillChangeInline,
  formatSkillLevel,
} from "../utils/formatSkillDisplay";
import {
  fetchMergedCanonicalMatchRowsDesc,
  fetchPerspectiveRowsForAthleteSeasonDesc,
} from "../utils/canonicalMatches";
import {
  fetchLeagueSkillMinMax,
  skillBarLinearFillPercent as linearSkillBarFillPercent,
  computeEffectiveSkillBarRange,
  SKILL_BAR_MIN,
} from "../utils/skillBarLinear";
import { authState } from "../utils/authState";
import { loadFreshTeamRankingsFromCache } from "../utils/leagueTeamRankingsCache";

export default {
  name: "LeagueRankings",
  components: {
    FloatingNav,
    "skeleton-loader": () => import("../components/SkeletonLoader"),
  },
  data() {
    return {
      defaultLeagueLogo: require("../assets/logo.svg"),
      leagueData: null,
      weightclasses: [],
      loading: true,
      poundTop: [],
      poundLoading: false,
      risingTop: [],
      risingLoading: false,
      expandedWeights: {},
      topFiveCache: {},
      loadingTopFive: {},
      leagueSkillMin: null,
      leagueSkillMax: null,
      teamTop: [],
      teamLoading: false,
      teamRankingsSeq: 0,
      teamRankingsDeferTimer: null,
      /** Season toolbar note for team column (fallback message or default copy). */
      teamRankingsSeasonHint: "",
    };
  },
  computed: {
    authenticated() {
      return authState.user != null;
    },
    leagueId() {
      return this.$route.params.leagueId || "";
    },
    leagueName() {
      return (this.leagueData && this.leagueData.Name) || "";
    },
    poundSkillBarEffectiveMin() {
      const nums = this.skillsFromTopRows(this.poundTop);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).low;
    },
    poundSkillBarEffectiveMax() {
      const nums = this.skillsFromTopRows(this.poundTop);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).high;
    },
    risingSkillBarEffectiveMin() {
      const nums = this.skillsFromTopRows(this.risingTop);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).low;
    },
    risingSkillBarEffectiveMax() {
      const nums = this.skillsFromTopRows(this.risingTop);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).high;
    },
    teamSkillBarEffectiveMin() {
      const nums = (this.teamTop || [])
        .map((t) => t.avgSkill)
        .filter(Number.isFinite);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).low;
    },
    teamSkillBarEffectiveMax() {
      const nums = (this.teamTop || [])
        .map((t) => t.avgSkill)
        .filter(Number.isFinite);
      return computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      ).high;
    },
    teamRankingsHeaderSubtitle() {
      if (this.teamRankingsSeasonHint)
        return this.teamRankingsSeasonHint;
      const sel = seasonFilterState.selected;
      if (isAllSeasons(sel)) {
        return "Best profile Skill per club at each weight (all seasons).";
      }
      return "Best profile Skill per club at each weight; only wrestlers who competed in the selected season count.";
    },
  },
  watch: {
    authenticated(isAuth) {
      if (!this.leagueId || !this.weightclasses || !this.weightclasses.length)
        return;
      if (isAuth) {
        this.loadPoundForPound();
        this.loadRisingStars();
        this._scheduleTeamRankings();
      } else {
        if (this.teamRankingsDeferTimer != null) {
          clearTimeout(this.teamRankingsDeferTimer);
          this.teamRankingsDeferTimer = null;
        }
        this.teamRankingsSeq += 1;
        this.poundTop = [];
        this.risingTop = [];
        this.teamTop = [];
        this.teamRankingsSeasonHint = "";
        this.teamRankingsCacheStamp = "";
        this.poundLoading = false;
        this.risingLoading = false;
        this.teamLoading = false;
      }
    },
    leagueId: {
      immediate: true,
      handler(id) {
        if (id) this.loadLeague();
        else {
          if (this.teamRankingsDeferTimer != null) {
            clearTimeout(this.teamRankingsDeferTimer);
            this.teamRankingsDeferTimer = null;
          }
          this.teamRankingsSeq += 1;
          this.leagueData = null;
          this.weightclasses = [];
          this.teamTop = [];
          this.teamRankingsSeasonHint = "";
          this.teamRankingsCacheStamp = "";
          this.poundTop = [];
          this.risingTop = [];
          this.poundLoading = false;
          this.risingLoading = false;
          this.teamLoading = false;
          this.loading = false;
        }
      },
    },
  },
  mounted() {
    this._seasonUnwatch = this.$watch(
      () => seasonFilterState.selected,
      () => {
        if (this.leagueId) {
          this.topFiveCache = {};
          this.expandedWeights = {};
          if (this.authenticated) {
            this.loadPoundForPound();
            this.loadRisingStars();
            this._scheduleTeamRankings();
          } else {
            this.poundTop = [];
            this.risingTop = [];
            this.teamTop = [];
            this.teamRankingsSeasonHint = "";
            this.teamRankingsCacheStamp = "";
            this.poundLoading = false;
            this.risingLoading = false;
            this.teamLoading = false;
          }
        }
      }
    );
  },
  beforeDestroy() {
    if (this._seasonUnwatch) this._seasonUnwatch();
    if (this.teamRankingsDeferTimer != null) {
      clearTimeout(this.teamRankingsDeferTimer);
      this.teamRankingsDeferTimer = null;
    }
  },
  methods: {
    formatSkillChangeInline,
    formatSkillLevel,
    async loadLeagueSkillBounds() {
      const league = this.leagueName;
      if (!league) {
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
        return;
      }
      const db = getFirestore();
      try {
        const { min, max } = await fetchLeagueSkillMinMax(db, league);
        this.leagueSkillMin = min;
        this.leagueSkillMax = max;
      } catch (_) {
        this.leagueSkillMin = null;
        this.leagueSkillMax = null;
      }
    },
    skillsFromTopRows(rows) {
      return (rows || [])
        .map((w) => {
          const s = w.data && w.data.Skill;
          return typeof s === "number" ? s : Number(s);
        })
        .filter((n) => Number.isFinite(n));
    },
    poundSkillBarPercent(w) {
      const s = w.data && w.data.Skill;
      const skill = typeof s === "number" ? s : Number(s);
      const val = Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      return linearSkillBarFillPercent(
        val,
        this.poundSkillBarEffectiveMax,
        this.poundSkillBarEffectiveMin
      );
    },
    risingSkillBarPercent(w) {
      const s = w.data && w.data.Skill;
      const skill = typeof s === "number" ? s : Number(s);
      const val = Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      return linearSkillBarFillPercent(
        val,
        this.risingSkillBarEffectiveMax,
        this.risingSkillBarEffectiveMin
      );
    },
    goBack() {
      this.$router.go(-1);
    },
    onLeagueLogoError(e) {
      if (e && e.target && this.defaultLeagueLogo)
        e.target.src = this.defaultLeagueLogo;
    },
    weightRoute(wc) {
      const weight =
        wc.Weight != null && wc.Weight !== "" ? String(wc.Weight) : "";
      const league = this.leagueName;
      if (!league || weight === "") return "/";
      return `/league/${encodeURIComponent(league)}/weight/${encodeURIComponent(
        weight
      )}`;
    },
    weightKey(wc) {
      const wv = rankWeightValueFromWc(wc);
      const w = wv != null && wv !== "" ? String(wv) : "";
      return `${this.leagueName}\t${w}`;
    },
    isExpanded(wc) {
      return !!this.expandedWeights[this.weightKey(wc)];
    },
    toggleExpand(wc) {
      const key = this.weightKey(wc);
      this.expandedWeights = {
        ...this.expandedWeights,
        [key]: !this.expandedWeights[key],
      };
      if (this.expandedWeights[key] && !this.topFiveCache[key]) {
        this.loadTopFive(wc);
      }
    },
    getTopFive(wc) {
      return this.topFiveCache[this.weightKey(wc)] || [];
    },
    weightTopFiveSkillBarPercent(w, wc) {
      const rows = this.getTopFive(wc) || [];
      const nums = this.skillsFromTopRows(rows);
      const { low, high } = computeEffectiveSkillBarRange(
        this.leagueSkillMin,
        this.leagueSkillMax,
        ...nums
      );
      const s = w.data && w.data.Skill;
      const skill = typeof s === "number" ? s : Number(s);
      const val = Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      return linearSkillBarFillPercent(val, high, low);
    },
    goToWrestler(id) {
      this.$router.push("/wrestler/" + id);
    },
    goToTeamLineup(t) {
      const school = (t && t.schoolExact && String(t.schoolExact).trim()) || "";
      if (!school || !this.leagueId) return;
      this.$router.push({
        name: "league-team-lineup",
        params: { leagueId: this.leagueId },
        query: { school },
      });
    },
    /**
     * @param {object} wc Weight class doc with Weight
     * @param {number|null} prefetchLimit If null, loads full roster (no Skill cap).
     */
    async fetchSortedAthletesForLeagueWeight(wc, prefetchLimit) {
      const league = this.leagueName;
      const weightVal = rankWeightValueFromWc(wc);
      if (!league || (weightVal !== 0 && !weightVal)) return [];
      const db = getFirestore();
      const weightStr = String(weightVal);
      const weightNum = Number(weightVal);
      const tryQuery = (wVal, useOrderBy) => {
        const constraints = [
          where("RankLeague", "==", league),
          where("RankWeightClass", "==", wVal),
        ];
        if (useOrderBy && prefetchLimit != null) {
          constraints.push(orderBy("Skill", "desc"));
          constraints.push(limit(prefetchLimit));
        }
        const q = query(collection(db, "athletes"), ...constraints);
        return getDocs(q).then((snap) => {
          const rows = [];
          snap.forEach((docSnap) =>
            rows.push({ id: docSnap.id, data: docSnap.data() })
          );
          if (!useOrderBy && rows.length) {
            rows.sort((a, b) => (b.data.Skill || 0) - (a.data.Skill || 0));
          }
          return rows;
        });
      };
      let rows = [];
      if (prefetchLimit == null) {
        rows = await tryQuery(weightVal, false).catch(() => []);
        if (rows.length === 0 && weightNum !== weightVal && !isNaN(weightNum)) {
          rows = await tryQuery(weightNum, false).catch(() => []);
        }
        if (rows.length === 0 && weightStr !== weightVal) {
          rows = await tryQuery(weightStr, false).catch(() => []);
        }
      } else {
        rows = await tryQuery(weightVal, true).catch(() =>
          tryQuery(weightVal, false)
        );
        if (rows.length === 0 && weightNum !== weightVal && !isNaN(weightNum)) {
          rows = await tryQuery(weightNum, true).catch(() =>
            tryQuery(weightNum, false)
          );
        }
        if (rows.length === 0 && weightStr !== weightVal) {
          rows = await tryQuery(weightStr, true).catch(() =>
            tryQuery(weightStr, false)
          );
        }
      }
      rows = await sortWrestlersByRankingRules(db, rows, {
        matchesPerAthleteLimit: LEAGUE_PAGE_MATCH_FETCH,
      });
      if (!isAllSeasons(seasonFilterState.selected)) {
        rows = await filterAthleteRowsBySeason(
          db,
          rows,
          seasonFilterState.selected
        );
      }
      return rows;
    },
    normalizeTeamSchoolKey(school) {
      const s = school == null ? "" : String(school).trim();
      if (!s) return "";
      return s.toLowerCase();
    },
    teamSkillBarPercent(t) {
      const val = Number.isFinite(t.avgSkill) ? t.avgSkill : SKILL_BAR_MIN;
      return linearSkillBarFillPercent(
        val,
        this.teamSkillBarEffectiveMax,
        this.teamSkillBarEffectiveMin
      );
    },
    _scheduleTeamRankings() {
      if (!this.authenticated) {
        if (this.teamRankingsDeferTimer != null) {
          clearTimeout(this.teamRankingsDeferTimer);
          this.teamRankingsDeferTimer = null;
        }
        this.teamTop = [];
        this.teamLoading = false;
        return;
      }
      if (this.teamRankingsDeferTimer != null) {
        clearTimeout(this.teamRankingsDeferTimer);
        this.teamRankingsDeferTimer = null;
      }
      this.teamRankingsDeferTimer = setTimeout(() => {
        this.teamRankingsDeferTimer = null;
        this.loadTeamRankings();
      }, 100);
    },
    /**
     * Team rankings only: athletes at this league + weight, sorted by profile Skill.
     * No match reads, no H2H sort, no season filter (same roster regardless of season menu).
     */
    fetchLeagueWeightAthletesOrderedBySkill(league, wc) {
      return fetchAllAthletesAtLeagueWeightOrderedBySkill(
        getFirestore(),
        league,
        wc
      );
    },
    _schoolStringFromAthleteData(data) {
      if (!data) return "";
      if (data.School != null && String(data.School).trim() !== "")
        return String(data.School).trim();
      if (data.Team != null && String(data.Team).trim() !== "")
        return String(data.Team).trim();
      return "";
    },
    /** First row per normalized school = highest Skill at this weight (rows already Skill-desc). */
    _bestRowPerTeamKeyFromSkillSorted(rows) {
      const map = new Map();
      for (const row of rows) {
        let key = this.normalizeTeamSchoolKey(
          this._schoolStringFromAthleteData(row.data)
        );
        if (!key) key = `noschool:${row.id}`;
        if (map.has(key)) continue;
        map.set(key, row);
      }
      return map;
    },
    /**
     * Exact School strings from league doc `TeamSchools` (see League Manager).
     * Enables RankLeague + RankWeightClass + School + Skill + limit(1) per team per weight.
     */
    teamSchoolsForIndexedTeamRankings() {
      const raw = this.leagueData && this.leagueData.TeamSchools;
      if (!Array.isArray(raw) || !raw.length) return null;
      const out = [];
      const seen = new Set();
      for (const s of raw) {
        if (s == null) continue;
        const t = String(s).trim();
        if (!t) continue;
        const k = this.normalizeTeamSchoolKey(t);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        out.push(t);
      }
      return out.length ? out : null;
    },
    fetchTopOneAthleteSchoolAtWeight(league, wc, schoolExact) {
      return fetchTopAthleteForSchoolAtWeight(
        getFirestore(),
        league,
        wc,
        schoolExact
      );
    },
    async fetchPerWeightTeamBestsIndexed(league, wcs, teamSchoolsExact, seq) {
      const perWeightTeamBest = [];
      for (const wc of wcs) {
        if (seq !== this.teamRankingsSeq) return null;
        const pairs = await Promise.all(
          teamSchoolsExact.map((schoolExact) =>
            this.fetchTopOneAthleteSchoolAtWeight(league, wc, schoolExact).then(
              (row) => ({ schoolExact, row })
            )
          )
        );
        const map = new Map();
        for (const { schoolExact, row } of pairs) {
          if (!row) continue;
          const key = this.normalizeTeamSchoolKey(schoolExact);
          if (!key) continue;
          map.set(key, row);
        }
        perWeightTeamBest.push(map);
      }
      return perWeightTeamBest;
    },
    async loadTopFive(wc) {
      const weightVal = rankWeightValueFromWc(wc);
      if (!this.leagueName || (weightVal !== 0 && !weightVal)) return;
      const key = this.weightKey(wc);
      this.loadingTopFive = { ...this.loadingTopFive, [key]: true };
      try {
        const rows = await this.fetchSortedAthletesForLeagueWeight(
          wc,
          TOP_FIVE_PREFETCH
        );
        this.topFiveCache = { ...this.topFiveCache, [key]: rows.slice(0, 5) };
      } catch (_) {
        this.topFiveCache = { ...this.topFiveCache, [key]: [] };
      } finally {
        this.loadingTopFive = { ...this.loadingTopFive, [key]: false };
      }
    },
    async loadTeamRankings() {
      const league = this.leagueName;
      const wcs = this.weightclasses;
      if (!this.authenticated) {
        this.teamTop = [];
        this.teamLoading = false;
        return;
      }
      if (!league || !wcs || !wcs.length) {
        this.teamTop = [];
        this.teamLoading = false;
        return;
      }
      const seq = ++this.teamRankingsSeq;
      this.teamLoading = true;
      this.teamRankingsSeasonHint = "";
      this.teamRankingsCacheStamp = "";
      const db = getFirestore();
      try {
        const cached = await loadFreshTeamRankingsFromCache(
          db,
          this.leagueId,
          seasonFilterState.selected
        );
        if (seq !== this.teamRankingsSeq) return;
        if (cached) {
          const list = Array.isArray(cached.teams) ? cached.teams : [];
          const usable = list.filter((t) => {
            if (!t || typeof t !== "object") return false;
            const a = t.avgSkill;
            const n = typeof a === "number" ? a : Number(a);
            return Number.isFinite(n);
          });
          if (usable.length > 0) {
            this.teamTop = usable.slice(0, 10);
            this.teamRankingsSeasonHint = cached.seasonHint || "";
            this.teamRankingsCacheStamp = cached.updatedLabel;
            return;
          }
        }

        let perWeightTeamBest;
        const indexedSchools = this.teamSchoolsForIndexedTeamRankings();
        if (indexedSchools && indexedSchools.length) {
          perWeightTeamBest = await this.fetchPerWeightTeamBestsIndexed(
            league,
            wcs,
            indexedSchools,
            seq
          );
          if (perWeightTeamBest == null || seq !== this.teamRankingsSeq) return;
          const indexedHasAny = perWeightTeamBest.some((m) => m && m.size > 0);
          if (!indexedHasAny) {
            const weightSnapshots = await Promise.all(
              wcs.map((wc) =>
                this.fetchLeagueWeightAthletesOrderedBySkill(league, wc)
              )
            );
            if (seq !== this.teamRankingsSeq) return;
            perWeightTeamBest = weightSnapshots.map((rows) =>
              this._bestRowPerTeamKeyFromSkillSorted(rows)
            );
          }
        } else {
          const weightSnapshots = await Promise.all(
            wcs.map((wc) =>
              this.fetchLeagueWeightAthletesOrderedBySkill(league, wc)
            )
          );
          if (seq !== this.teamRankingsSeq) return;
          perWeightTeamBest = weightSnapshots.map((rows) =>
            this._bestRowPerTeamKeyFromSkillSorted(rows)
          );
        }

        const candidateIds = new Set();
        const candidates = [];
        perWeightTeamBest.forEach((map) => {
          map.forEach((row) => {
            if (row && row.id && !candidateIds.has(row.id)) {
              candidateIds.add(row.id);
              candidates.push(row);
            }
          });
        });

        const clonePerWeightMaps = (maps) => maps.map((m) => new Map(m));
        const perWeightTeamBestBackup = clonePerWeightMaps(perWeightTeamBest);

        let seasonFilterApplied = false;
        let allowedIds = null;
        if (!isAllSeasons(seasonFilterState.selected) && candidates.length) {
          seasonFilterApplied = true;
          const filtered = await filterAthleteRowsBySeason(
            db,
            candidates,
            seasonFilterState.selected
          );
          if (seq !== this.teamRankingsSeq) return;
          allowedIds = new Set(filtered.map((r) => r.id));
        }

        if (allowedIds) {
          perWeightTeamBest.forEach((map) => {
            for (const [k, row] of [...map.entries()]) {
              if (!allowedIds.has(row.id)) map.delete(k);
            }
          });
        }

        const mapsStillHaveRows = perWeightTeamBest.some(
          (m) => m && m.size > 0
        );
        if (!mapsStillHaveRows && seasonFilterApplied) {
          perWeightTeamBest.splice(
            0,
            perWeightTeamBest.length,
            ...clonePerWeightMaps(perWeightTeamBestBackup)
          );
          this.teamRankingsSeasonHint =
            "No wrestler in any top slot had a match this season — showing all-season picks.";
        }

        const displayByKey = new Map();
        perWeightTeamBest.forEach((map) => {
          map.forEach((row, teamKey) => {
            if (!displayByKey.has(teamKey)) {
              const d = this._schoolStringFromAthleteData(row.data);
              displayByKey.set(teamKey, d || "Unknown");
            }
          });
        });

        const totalWcs = perWeightTeamBest.length;
        const teams = [];
        displayByKey.forEach((schoolDisplay, teamKey) => {
          let sum = 0;
          let weightsUsed = 0;
          let schoolExact = "";
          for (let w = 0; w < perWeightTeamBest.length; w++) {
            const pick = perWeightTeamBest[w].get(teamKey);
            if (pick) {
              const s = pick.data && pick.data.Skill;
              const n = typeof s === "number" ? s : Number(s);
              if (Number.isFinite(n)) {
                sum += n;
                weightsUsed += 1;
              }
              if (!schoolExact && pick.data) {
                const sx = this._schoolStringFromAthleteData(pick.data);
                if (sx) schoolExact = sx;
              }
            }
          }
          if (weightsUsed === 0) return;
          if (!schoolExact) schoolExact = schoolDisplay;
          teams.push({
            teamKey,
            schoolDisplay,
            schoolExact,
            avgSkill: sum / totalWcs,
            weightsUsed,
          });
        });
        teams.sort((a, b) => b.avgSkill - a.avgSkill);
        if (seq !== this.teamRankingsSeq) return;
        this.teamTop = teams.slice(0, 10);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[LeagueRankings] loadTeamRankings failed", e);
        if (seq === this.teamRankingsSeq) this.teamTop = [];
      } finally {
        if (seq === this.teamRankingsSeq) this.teamLoading = false;
      }
    },
    async loadLeague() {
      if (!this.leagueId) return;
      this.loading = true;
      const db = getFirestore();
      try {
        const leagueRef = doc(db, "leagues", this.leagueId);
        const leagueSnap = await getDoc(leagueRef);
        if (leagueSnap.exists()) {
          this.leagueData = leagueSnap.data();
          const wcRef = collection(
            db,
            "leagues",
            this.leagueId,
            "weightclasses"
          );
          const q = query(wcRef, orderBy("Sort"), orderBy("Weight"));
          const wcSnap = await getDocs(q);
          this.weightclasses = [];
          wcSnap.forEach((d) => {
            this.weightclasses.push({ id: d.id, ...d.data() });
          });
        } else {
          this.leagueData = null;
          this.weightclasses = [];
        }

        // Load pound-for-pound and rising-stars summaries for this league
        this.loadPoundForPound();
        this.loadRisingStars();
        this.loadLeagueSkillBounds();
        this._scheduleTeamRankings();
      } catch (_) {
        this.leagueData = null;
        this.weightclasses = [];
        this.teamTop = [];
      } finally {
        this.loading = false;
      }
    },
    async loadPoundForPound() {
      const league = this.leagueName;
      if (!this.authenticated) {
        this.poundTop = [];
        this.poundLoading = false;
        return;
      }
      if (!league) {
        this.poundTop = [];
        this.poundLoading = false;
        return;
      }
      this.poundLoading = true;
      const db = getFirestore();
      const athletesRef = collection(db, "athletes");
      try {
        let rows = [];
        try {
          const q = query(
            athletesRef,
            where("RankLeague", "==", league),
            orderBy("Skill", "desc"),
            limit(P4P_PREFETCH)
          );
          const snap = await getDocs(q);
          snap.forEach((d) => rows.push({ id: d.id, data: d.data() }));
        } catch (_) {
          const q = query(athletesRef, where("RankLeague", "==", league));
          const snap = await getDocs(q);
          snap.forEach((d) => rows.push({ id: d.id, data: d.data() }));
          rows.sort((a, b) => (b.data.Skill || 0) - (a.data.Skill || 0));
          rows = rows.slice(0, P4P_PREFETCH);
        }
        rows = await sortWrestlersByRankingRules(db, rows, {
          matchesPerAthleteLimit: LEAGUE_PAGE_MATCH_FETCH,
        });
        if (!isAllSeasons(seasonFilterState.selected)) {
          rows = await filterAthleteRowsBySeason(
            db,
            rows,
            seasonFilterState.selected
          );
        }
        this.poundTop = rows.slice(0, 5);
      } catch (_) {
        this.poundTop = [];
      } finally {
        this.poundLoading = false;
      }
    },
    async loadRisingStars() {
      const league = this.leagueName;
      if (!this.authenticated) {
        this.risingTop = [];
        this.risingLoading = false;
        return;
      }
      if (!league) {
        this.risingTop = [];
        this.risingLoading = false;
        return;
      }
      this.risingLoading = true;
      const db = getFirestore();
      const athletesRef = collection(db, "athletes");
      try {
        // Sample high- and low-skill athletes; true global top-3 risers may sit in the middle (tradeoff for read cost).
        let athletes = [];
        const seen = new Set();
        const addRows = (snap) => {
          snap.forEach((d) => {
            if (seen.has(d.id)) return;
            seen.add(d.id);
            athletes.push({ id: d.id, data: d.data() });
          });
        };
        try {
          const [snapHigh, snapLow] = await Promise.all([
            getDocs(
              query(
                athletesRef,
                where("RankLeague", "==", league),
                orderBy("Skill", "desc"),
                limit(RISING_PREFETCH_EACH_END)
              )
            ),
            getDocs(
              query(
                athletesRef,
                where("RankLeague", "==", league),
                orderBy("Skill", "asc"),
                limit(RISING_PREFETCH_EACH_END)
              )
            ),
          ]);
          addRows(snapHigh);
          addRows(snapLow);
        } catch (_) {
          const q = query(athletesRef, where("RankLeague", "==", league));
          const snap = await getDocs(q);
          const tmp = [];
          snap.forEach((d) => tmp.push({ id: d.id, data: d.data() }));
          tmp.sort((a, b) => (b.data.Skill || 0) - (a.data.Skill || 0));
          tmp.slice(0, RISING_PREFETCH_EACH_END * 2).forEach((row) => {
            if (seen.has(row.id)) return;
            seen.add(row.id);
            athletes.push(row);
          });
        }

        if (!isAllSeasons(seasonFilterState.selected)) {
          athletes = await filterAthleteRowsBySeason(
            db,
            athletes,
            seasonFilterState.selected
          );
        }

        const seasonSel = seasonFilterState.selected;

        // Fetch last matches for all athletes in parallel (one batch instead of N sequential)
        const matchPromises = athletes.map(async (a) => {
          try {
            let matches;
            if (!isAllSeasons(seasonSel)) {
              const seasonVars = seasonShortToStoredVariants(seasonSel);
              matches =
                seasonVars.length > 0
                  ? await fetchPerspectiveRowsForAthleteSeasonDesc(
                      db,
                      a.id,
                      seasonVars,
                      24
                    )
                  : [];
            } else {
              const merged = await fetchMergedCanonicalMatchRowsDesc(
                db,
                a.id,
                8
              );
              matches = merged.map((r) => r.data);
            }
            if (matches.length) {
              matches.sort((m1, m2) => {
                const d1 = m1.MatchDate
                  ? typeof m1.MatchDate.toDate === "function"
                    ? m1.MatchDate.toDate().getTime()
                    : new Date(m1.MatchDate).getTime()
                  : 0;
                const d2 = m2.MatchDate
                  ? typeof m2.MatchDate.toDate === "function"
                    ? m2.MatchDate.toDate().getTime()
                    : new Date(m2.MatchDate).getTime()
                  : 0;
                return d2 - d1;
              });
            }
            let forDelta = matches;
            if (!isAllSeasons(seasonSel)) {
              const selShort = normalizeSeasonToShort(String(seasonSel).trim());
              forDelta = matches.filter(
                (m) =>
                  normalizeSeasonToShort(String(m.Season || "").trim()) ===
                  selShort
              );
            }
            const recent = forDelta.slice(0, 3);
            let delta = 0;
            recent.forEach((m) => {
              const c = typeof m.SkillChange === "number" ? m.SkillChange : 0;
              delta += c;
            });
            if (delta > 0) {
              const trendMatches = !isAllSeasons(seasonSel)
                ? forDelta
                : matches;
              const trendPoints = this.buildTrendPoints(
                trendMatches,
                a.data.Skill
              );
              return { id: a.id, data: a.data, delta, trendPoints };
            }
          } catch (_) {
            // ignore this wrestler if matches fail
          }
          return null;
        });

        const resolved = await Promise.all(matchPromises);
        const results = resolved.filter(Boolean);
        results.sort((a, b) => (b.delta || 0) - (a.delta || 0));
        this.risingTop = results.slice(0, 5);
      } catch (_) {
        this.risingTop = [];
      } finally {
        this.risingLoading = false;
      }
    },
    buildTrendPoints(matches, currentSkill) {
      if (!matches || matches.length === 0) return [];
      const withDate = matches
        .filter((m) => m && m.MatchDate != null)
        .map((m) => {
          const d = m.MatchDate;
          const ts =
            typeof d.toDate === "function"
              ? d.toDate().getTime()
              : d && d.seconds
              ? d.seconds * 1000
              : new Date(d).getTime();
          return {
            ts,
            change: typeof m.SkillChange === "number" ? m.SkillChange : 0,
          };
        });
      if (withDate.length === 0) return [];
      withDate.sort((a, b) => a.ts - b.ts);
      const points = [];
      let skill = 1200;
      points.push({ ts: withDate[0].ts - 1, skill });
      withDate.forEach((m) => {
        skill += m.change;
        points.push({ ts: m.ts, skill });
      });
      const current = Number(currentSkill);
      if (typeof current === "number" && !isNaN(current) && points.length > 0) {
        points.push({ ts: Date.now(), skill: current });
      }
      return points;
    },
    risingSparklinePath(points) {
      if (!points || points.length === 0) return "";
      if (points.length === 1) return "M 0 12 L 80 12";
      const skills = points.map((p) => p.skill);
      const minS = Math.min(1200, ...skills);
      const maxS = Math.max(1200, ...skills);
      const range = maxS - minS || 1;
      const pad = range * 0.1;
      const lo = minS - pad;
      const hi = maxS + pad;
      const span = hi - lo || 1;
      const w = 80;
      const h = 24;
      const x = (i) => (i / (points.length - 1)) * w;
      const y = (s) => h - ((s - lo) / span) * h;
      return points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.skill)}`)
        .join(" ");
    },
  },
};
</script>

<style scoped>
.league-rankings-page {
  min-height: 100%;
  background: #f5f5f7;
  padding-bottom: 2rem;
}

.league-rankings-header {
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.back-btn {
  flex-shrink: 0;
}

.league-rankings-title-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.league-rankings-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
}

.league-rankings-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
}

.league-rankings-subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  width: 100%;
}

.league-rankings-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;
}

.league-rankings-main {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.team-rankings-skill-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.3;
}

.team-rankings-cache-stamp {
  display: block;
  margin-top: 2px;
  font-size: 0.7rem;
  line-height: 1.25;
}

.team-ranking-row {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 8px;
}

.team-ranking-row--clickable {
  cursor: pointer;
  transition: background 0.15s ease;
}

.team-ranking-row--clickable:hover {
  background: #f5f5f5;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
  gap: 1rem;
}

.weight-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rankings-col-p4p {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rankings-col-p4p > .pound-block {
  margin-bottom: 0;
}

@media (min-width: 960px) {
  .league-rankings-content {
    max-width: 1280px;
  }
  .league-rankings-main {
    gap: 1rem;
  }
  .weight-links {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 1rem;
  }
  .rankings-col {
    padding-top: 0;
    margin-top: 0;
  }
  .rankings-col > * {
    margin-top: 0;
    padding-top: 0;
  }
  .rankings-col-p4p {
    flex: 1 1 0;
    min-width: 0;
    order: 1;
  }
  .rankings-col-weightclasses {
    flex: 2 1 0;
    min-width: 0;
    order: 2;
  }
  .weight-classes-inner {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .weight-classes-inner > .weight-class-block:first-child {
    margin-top: 0;
  }
  .rankings-col-team {
    flex: 1 1 0;
    min-width: 0;
    order: 3;
  }
}

.weight-class-block {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.pound-block {
  margin-bottom: 0.5rem;
}

.weight-link-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  transition: background 0.15s ease;
}

.weight-link-card--stacked-heading {
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
}

.weight-link-card--stacked-heading .weight-link-label {
  width: 100%;
  line-height: 1.25;
}

.weight-link-card:hover {
  background: #fafafa;
}

.weight-link-label {
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
}

.weight-link-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.pound-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.35;
  width: 100%;
}

.weight-link-card--stacked-heading .pound-subtitle {
  max-width: 100%;
}

.expand-btn {
  min-width: 36px !important;
}

.full-rankings-btn {
  font-size: 0.875rem;
  font-weight: 600;
  color: #c62828;
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.full-rankings-btn:hover {
  background: #fef2f2;
}

.top-five-wrap {
  border-top: 1px solid #f0f0f0;
  padding: 8px 12px 12px;
}

.top-five-loading,
.top-five-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: 0.9rem;
}

.top-five-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.top-five-row {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.top-five-row:hover {
  background: #f5f5f5;
}

.top-five-rank {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  line-height: 1;
}

.top-five-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
  flex-shrink: 0;
}

.top-five-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.top-five-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1f2937;
}

.top-five-record {
  font-size: 0.8rem;
  color: #6b7280;
}

.top-five-skill-bar.skill-bar--linear {
  width: 100%;
  max-width: 220px;
  min-width: 100px;
  height: 14px;
  position: relative;
}

.top-five-skill-bar .skill-bar-linear-track {
  height: 14px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  position: relative;
}

.top-five-skill-bar .skill-bar-linear-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 0;
  background: linear-gradient(
    90deg,
    #22c55e 0%,
    #a3e635 22%,
    #facc15 48%,
    #f97316 74%,
    #b91c1c 100%
  );
  transition: width 0.25s ease;
}

.top-five-skill-bar .skill-bar-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 600;
  color: #111827;
  line-height: 1;
  pointer-events: none;
}

/* Weight-class “top 5” rows: skill bar full width of row (all breakpoints) */
.rankings-col-weightclasses .top-five-skill-bar.skill-bar--linear {
  width: 100%;
  max-width: none;
  min-width: 0;
  align-self: stretch;
}

.rankings-col-weightclasses .top-five-skill-bar .skill-bar-linear-track {
  width: 100%;
}

/* Pound for Pound: skill bar full width of row (all breakpoints) */
.rankings-col-p4p .top-five-skill-bar.skill-bar--linear {
  width: 100%;
  max-width: none;
  min-width: 0;
  align-self: stretch;
}

.rankings-col-p4p .top-five-skill-bar .skill-bar-linear-track {
  width: 100%;
}

/* Team rankings: full-width skill bar when layout stacks (phones / narrow viewports) */
@media (max-width: 959px) {
  .team-rankings-block .top-five-skill-bar.skill-bar--linear {
    width: 100%;
    max-width: none;
    min-width: 0;
    align-self: stretch;
  }

  .team-rankings-block .top-five-skill-bar .skill-bar-linear-track {
    width: 100%;
  }
}

.rising-sparkline-wrap {
  flex-shrink: 0;
  width: 80px;
  height: 24px;
  margin-right: 4px;
}
.rising-sparkline {
  width: 80px;
  height: 24px;
  display: block;
  color: #b91c1c;
  opacity: 0.85;
}

.top-five-chevron {
  color: #d1d5db;
  flex-shrink: 0;
}

.empty-state-wrap {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-state {
  margin: 0;
  color: #6b7280;
  font-size: 1rem;
}

/* Dark mode */
.theme--dark .league-rankings-page {
  background: #121212;
}
.theme--dark .league-rankings-header {
  background: #1e1e1e;
}
.theme--dark .league-rankings-title {
  color: #e5e7eb;
}
.theme--dark .league-rankings-subtitle {
  color: #9ca3af;
}
.theme--dark .weight-class-block {
  background: #1e1e1e;
}
.theme--dark .weight-link-card:hover {
  background: #2d2d2d;
}
.theme--dark .weight-link-label {
  color: #e5e7eb;
}
.theme--dark .pound-subtitle {
  color: #9ca3af;
}
.theme--dark .team-rankings-skill-hint {
  color: #9ca3af;
}
.theme--dark .team-rankings-cache-stamp {
  color: #9ca3af;
}
.theme--dark .full-rankings-btn {
  color: #f87171;
}
.theme--dark .full-rankings-btn:hover {
  background: rgba(248, 113, 113, 0.12);
}
.theme--dark .top-five-wrap {
  border-top-color: rgba(255, 255, 255, 0.08);
}
.theme--dark .top-five-row:hover {
  background: #2d2d2d;
}
.theme--dark .team-ranking-row--clickable:hover {
  background: #2d2d2d;
}
.theme--dark .top-five-name {
  color: #e5e7eb;
}
.theme--dark .top-five-record {
  color: #9ca3af;
}
.theme--dark .top-five-skill-bar .skill-bar-linear-track {
  background: #374151;
}
.theme--dark .top-five-skill-bar .skill-bar-label {
  color: #f3f4f6;
}
.theme--dark .rising-sparkline {
  color: #f87171;
}
.theme--dark .empty-state {
  color: #9ca3af;
}
</style>
